import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GridApi, SelectionChangedEvent } from 'ag-grid-enterprise';
import { assign, each, map } from 'lodash';
import { Card } from 'src/app/interfaces/card';
import { NotificationRecipient } from 'src/app/interfaces/models/notification-recipient';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';

import { Notification } from '../../../interfaces/models/notification';

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NotificationCenterComponent implements OnInit {
  constructor(
    private NotificationService: NotificationService,
    private UtilityService: UtilityService,
    private UserService: UserService,
    private ModalService: NgbModal
  ) {}

  notificationCollection!: Notification[];
  notificationCardCollection!: Card[];

  notificationModel: Notification = {
    id: null,
    type: null,
    title: null,
    bodyText: null,
    startDate: null,
    endDate: null,
    isAllUsers: false,
    isArchived: false,
    NotificationRecipients: []
  };

  selectedIdMap: {
    VendorId: Set<number>;
    CompanyId: Set<number>;
    UserId: Set<number>;
  } = {
    VendorId: new Set<number>(),
    CompanyId: new Set<number>(),
    UserId: new Set<number>()
  };

  notificationCenterForm!: FormGroup;

  checkboxModel = {
    everyoneCheckbox: false,
    clientsGridCheckbox: false,
    vendorsGridCheckbox: false,
    usersGridCheckbox: false
  };

  // When data is refreshed, reset the form.
  refresh() {
    this.resetForm();
    return this.NotificationService.getNotifications({
      includeNotificationRecipients: true
    }).subscribe({
      next: (data: any) => {
        this.notificationCollection = data;
        this.notificationCardCollection = map(
          this.notificationCollection,
          (n, index) =>
            this.UtilityService.mapValuesToCard(n.title, n.bodyText, n)
        );
      }
    });
  }

  archiveNotification(Notification: Notification) {
    const confirmModal = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    assign(confirmModal.componentInstance, {
      displayText: 'Are you sure you want to archive this Notification?'
    });

    confirmModal.result.then((confirm) => {
      if (confirm) {
        this.NotificationService.addEditNotification(
          assign(Notification, { isArchived: true })
        ).subscribe({
          next: () => {
            this.refresh();
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.notificationCenterForm = new FormGroup({
      id: new FormControl({
        value: this.notificationModel.id,
        disabled: false
      }),
      type: new FormControl({
        value: this.notificationModel.type,
        disabled: false
      }),
      title: new FormControl({
        value: this.notificationModel.title,
        disabled: false
      }),
      bodyText: new FormControl(
        { value: this.notificationModel.bodyText, disabled: false },
        Validators.required
      ),
      startDate: new FormControl({
        value: this.notificationModel.startDate,
        disabled: false
      }),
      endDate: new FormControl({
        value: this.notificationModel.endDate,
        disabled: false
      }),
      isAllUsers: new FormControl({
        value: this.notificationModel.isAllUsers,
        disabled: false
      }),
      isArchived: new FormControl({
        value: this.notificationModel.isArchived,
        disabled: false
      })
    });
    this.refresh();
  }

  onAllUsersChange() {
    this.clearAllSelectedIdMap();
    this.updateCheckboxModel();
  }

  resetForm() {
    // Reset to default values.
    this.notificationCenterForm.reset(this.notificationModel);
    this.clearAllSelectedIdMap();
    this.updateCheckboxModel();
  }

  clearSelectedIdMap(idType: 'CompanyId' | 'VendorId' | 'UserId') {
    this.selectedIdMap[idType].clear();
  }

  clearAllSelectedIdMap() {
    this.clearSelectedIdMap('CompanyId');
    this.clearSelectedIdMap('VendorId');
    this.clearSelectedIdMap('UserId');
  }

  updateCheckboxModel() {
    assign(this.checkboxModel, {
      clientsGridCheckbox: this.selectedIdMap['CompanyId'].size > 0,
      vendorsGridCheckbox: this.selectedIdMap['VendorId'].size > 0,
      usersGridCheckbox: this.selectedIdMap['UserId'].size > 0
    });
  }

  cardClicked(card: Card) {
    this.resetForm();
    if (card.isSelected && card.record) {
      this.notificationCenterForm.patchValue(card.record);

      each(card.record.NotificationRecipients, (recipient) => {
        if (recipient.CompanyId) {
          this.selectedIdMap['CompanyId'].add(recipient.CompanyId);
        }
        if (recipient.VendorId) {
          this.selectedIdMap['VendorId'].add(recipient.VendorId);
        }
        if (recipient.UserId) {
          this.selectedIdMap['UserId'].add(recipient.UserId);
        }
      });
    }
    this.updateCheckboxModel();
  }

  // IMPORTANT setup the getSelectedNodes function (if possible) to perform a query if using server side
  // implementation. This will need a lot of thought.
  // OR
  // Set up a special function that is able to retrieve all primary keys (per record) for all rows in a grid
  // if using server sided pagination.

  setSelectedIdMap(
    idType: 'CompanyId' | 'VendorId' | 'UserId',
    api: GridApi<any>
  ) {
    this.clearSelectedIdMap(idType);
    api.getSelectedNodes().forEach((n) => {
      this.selectedIdMap[idType].add(n.data.id);
    });
  }

  clientGridSelectionChanged(event: SelectionChangedEvent) {
    this.setSelectedIdMap('CompanyId', event.api);
  }

  vendorGridSelectionChanged(event: SelectionChangedEvent) {
    this.setSelectedIdMap('VendorId', event.api);
  }

  userGridSelectionChanged(event: SelectionChangedEvent) {
    this.setSelectedIdMap('UserId', event.api);
  }

  convertIdCollectionToNotificationRecipientCollection(
    idType: 'CompanyId' | 'VendorId' | 'UserId',
    idCollection: number[]
  ): NotificationRecipient[] {
    const notificationRecipients: NotificationRecipient[] = map(
      idCollection,
      (id) =>
        ({
          [idType]: id,
          NotificationId: null
        }) as NotificationRecipient
    );
    return notificationRecipients;
  }

  convertSelectedIdMapToNotificationRecipientCollection(): NotificationRecipient[] {
    const notificationRecipients: NotificationRecipient[] = [];
    this.selectedIdMap['CompanyId'].forEach((selectedId: number) => {
      const notificationRecipient: NotificationRecipient = {
        CompanyId: selectedId,
        NotificationId: null
      };
      notificationRecipients.push(notificationRecipient);
    });
    this.selectedIdMap['VendorId'].forEach((selectedId: number) => {
      const notificationRecipient: NotificationRecipient = {
        VendorId: selectedId,
        NotificationId: null
      };
      notificationRecipients.push(notificationRecipient);
    });
    this.selectedIdMap['UserId'].forEach((selectedId: number) => {
      const notificationRecipient: NotificationRecipient = {
        UserId: selectedId,
        NotificationId: null
      };
      notificationRecipients.push(notificationRecipient);
    });
    return notificationRecipients;
  }

  convertDateObjectToDate(dateObject: {
    day: number;
    month: number;
    year: number;
  }) {
    const { day, month, year } = dateObject;
    return new Date(year, month, day);
  }

  // This function is responsible for creating the notification.
  sendNotification() {
    const notification = this.notificationCenterForm.value;
    const { startDate, endDate } = notification;
    if (startDate) {
      assign(notification, {
        startDate: this.convertDateObjectToDate(startDate)
      });
    }
    if (endDate) {
      assign(notification, { endDate: this.convertDateObjectToDate(endDate) });
    }

    // From here, clear out all form values. Refresh. Display record on side panel.
    return this.NotificationService.addEditNotification(
      this.notificationCenterForm.value
    ).subscribe({
      next: (notificationData: any) => {
        // If Clients/Vendors/Users have been selected, create Notification Recipient records.
        if (!this.notificationCenterForm.get('isAllUsers')!.value) {
          return this.NotificationService.addEditNotificationRecipients(
            notificationData.id,
            [
              ...this.convertIdCollectionToNotificationRecipientCollection(
                'CompanyId',
                Array.from(this.selectedIdMap['CompanyId'])
              ),
              ...this.convertIdCollectionToNotificationRecipientCollection(
                'VendorId',
                Array.from(this.selectedIdMap['VendorId'])
              ),
              ...this.convertIdCollectionToNotificationRecipientCollection(
                'UserId',
                Array.from(this.selectedIdMap['UserId'])
              )
            ]
          ).subscribe({
            next: (data: any) => this.refresh(),
            error: (data: any) => {
              console.log(data);
            }
          });
        } else {
          // If 'All Users' checkbox has been checked, retrieve all UserIds and create Notification Recipient records
          // from them.
          return this.UserService.getAllUserIds().subscribe({
            next: (data: any) => {
              return this.NotificationService.addEditNotificationRecipients(
                notificationData.id,
                this.convertIdCollectionToNotificationRecipientCollection(
                  'UserId',
                  data
                )
              ).subscribe({
                next: (data: any) => this.refresh(),
                error: (data: any) => {
                  console.log(data);
                }
              });
            }
          });
        }
      },
      error: (data: any) => {
        console.log(data);
      }
    });
  }
}
